/* The business at a glance, over a period you chose.
 *
 * What was here before was ERPNext's 85 generic number cards, all-time and
 * unfiltered, with the charts crammed into 320px tiles. Two things were wrong
 * with that and both were reported: the figures answered a question nobody
 * asks ("every invoice we have ever raised"), and there was no way to say
 * *when*.
 *
 * So the period comes first and everything on the screen obeys it. It is a
 * **fiscal year**, defaulting to the current one, because that is the unit
 * Indian books are kept and closed in — and because landing on all of time
 * since the tenant started is how "is this number right?" becomes a support
 * call.
 *
 * Every figure is ERPNext's own: the tiles sum the same GL entries the Profit
 * and Loss Statement sums, and each one carries the sentence that says what
 * was added up and a way through to the rows behind it. A dashboard that adds
 * its own numbers up is a second set of books.
 */
import { useEffect, useState } from 'react'
import { ChartTile, formatCurrency, formatNumber, useErp } from '@jackdesk/erp-kit'
import type { Overview, OverviewTile } from '@jackdesk/erp-kit'
import { Failed, Loading } from '@jackdesk/shell'

const MODULES = ['Accounts', 'Selling', 'Buying', 'Stock'] as const

export function Dashboard({ onOpen, onGo }: {
  onOpen: (doctype: string) => void
  onGo?: (to: string) => void
}) {
  const erp = useErp()
  const [module, setModule] = useState<string>('Accounts')
  const [year, setYear] = useState<string>('')
  const [company, setCompany] = useState<string>('')
  const [overview, setOverview] = useState<Overview | null>(null)
  const [failed, setFailed] = useState(false)
  const [charts, setCharts] = useState<{ name: string; chart_name?: string; type?: string }[] | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setOverview(null); setFailed(false)
    erp.overview({ fiscalYear: year || undefined, company: company || undefined }, controller.signal)
      .then(setOverview)
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed(true) })
    return () => controller.abort()
  }, [erp, year, company])

  useEffect(() => {
    const controller = new AbortController()
    setCharts(null)
    erp.chartCatalogue(module, controller.signal)
      .then((r) => setCharts((r.charts ?? []).slice(0, 4)))
      .catch(() => setCharts([]))
    return () => controller.abort()
  }, [erp, module])

  const period = overview?.period
  const hero = overview?.tiles.find((t) => t.emphasis)
  const rest = overview?.tiles.filter((t) => !t.emphasis) ?? []

  return (
    <div className="page" style={{ display: 'grid', gap: 20 }}>
      <div>
        <h1>Dashboard</h1>
        <p className="muted small" style={{ marginTop: 4 }}>
          Your own figures, from the same ledger everything else writes to.
        </p>
      </div>

      {/* Filters in one row above everything they change. */}
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
        <label className="stack" style={{ gap: 4 }}>
          <span className="small muted">Financial year</span>
          <select className="select" style={{ minWidth: 150 }} value={year}
                  onChange={(e) => setYear(e.target.value)}>
            {(overview?.fiscal_years ?? []).map((y) => (
              <option key={y.name} value={y.name}>{y.name}</option>
            ))}
            {overview && overview.fiscal_years.length === 0 ? <option value="">None set up</option> : null}
          </select>
        </label>
        {overview && overview.companies.length > 1 ? (
          <label className="stack" style={{ gap: 4 }}>
            <span className="small muted">Company</span>
            <select className="select" style={{ minWidth: 190 }} value={company || overview.company || ''}
                    onChange={(e) => setCompany(e.target.value)}>
              {overview.companies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        ) : null}
        {period?.from_date ? (
          <span className="small muted" style={{ paddingBottom: 8 }}>
            {niceDate(period.from_date)} to {niceDate(period.to_date)}
          </span>
        ) : null}
      </div>

      {failed ? <Failed message="The figures could not be loaded." />
        : overview === null ? <Loading rows={4} label="Working out the figures" />
        : overview.fiscal_years.length === 0 ? (
          <p className="small muted">
            No financial year has been set up yet, so there is no period to report over.
          </p>
        ) : (
          <>
            {hero ? <Hero tile={hero} onGo={onGo} onOpen={onOpen} /> : null}
            <div className="erp-cards">
              {rest.map((t) => <Tile key={t.key} tile={t} onGo={onGo} onOpen={onOpen} />)}
            </div>
          </>
        )}

      <section style={{ display: 'grid', gap: 12 }}>
        <div className="row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Trends</h2>
          <select className="select" style={{ maxWidth: 190 }} aria-label="Part of the business"
                  value={module} onChange={(e) => setModule(e.target.value)}>
            {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        {charts === null ? <Loading rows={2} label="Loading charts" />
          : charts.length === 0 ? <p className="small muted">No charts for {module}.</p>
          : (
            /* Two across at most, so a chart is big enough to read. Four in a
               row at 320px was the complaint: axis labels collided and a
               trend was a scribble. */
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))' }}>
              {charts.map((c) => (
                <ChartTile key={c.name} name={c.name} title={c.chart_name ?? c.name} type={c.type}
                           height={260} />
              ))}
            </div>
          )}
      </section>
    </div>
  )
}

/** The one number the screen leads with. Exactly one, and the biggest thing
 *  on the page — a headline figure competing with its own label is a label. */
function Hero({ tile, onGo, onOpen }: TileProps) {
  return (
    <div className="card card-pad" style={{ display: 'grid', gap: 6 }}>
      <div className="small muted">{tile.label} · this financial year</div>
      <div style={{ fontSize: 48, fontWeight: 650, letterSpacing: '-0.03em', lineHeight: 1.05,
                    color: negative(tile) ? 'var(--danger)' : undefined }}>
        {figure(tile)}
      </div>
      <div className="small muted">{tile.ok ? tile.basis : (tile.why ?? 'Could not be worked out')}</div>
      <Behind tile={tile} onGo={onGo} onOpen={onOpen} />
    </div>
  )
}

function Tile({ tile, onGo, onOpen }: TileProps) {
  return (
    <div className="card card-pad" style={{ display: 'grid', gap: 3, alignContent: 'start' }}>
      <div className="small muted truncate" title={tile.label}>{tile.label}</div>
      {/* Proportional figures, not tabular: at this size `tabular-nums` gives
          every digit the width of a zero and a short number reads loose. */}
      <div style={{ fontSize: 26, fontWeight: 650, letterSpacing: '-0.02em',
                    color: !tile.ok ? 'var(--ink-3)'
                           : negative(tile) ? 'var(--danger)' : undefined }}>
        {figure(tile)}
      </div>
      <div className="small muted" style={{ lineHeight: 1.35 }}>
        {tile.ok ? tile.basis : (tile.why ?? 'Could not be worked out')}
      </div>
      <Behind tile={tile} onGo={onGo} onOpen={onOpen} />
    </div>
  )
}

/** The rows a figure was worked out from. A number nobody can get behind is
 *  one somebody re-checks by hand every time, which is the same as not having
 *  it. */
function Behind({ tile, onGo, onOpen }: TileProps) {
  if (!tile.ok) return null
  const links = []
  if (tile.report && onGo) {
    links.push(
      <button key="r" className="linkish small"
              onClick={() => onGo(`/report/${encodeURIComponent(tile.report!)}`)}>
        {tile.report}
      </button>)
  }
  if (tile.doctype) {
    links.push(
      <button key="d" className="linkish small" onClick={() => onOpen(tile.doctype!)}>
        {tile.doctype}
      </button>)
  }
  if (!links.length) return null
  return <div className="row" style={{ gap: 10, marginTop: 4, flexWrap: 'wrap' }}>{links}</div>
}

type TileProps = {
  tile: OverviewTile
  onGo?: (to: string) => void
  onOpen: (doctype: string) => void
}

/** Money in the red. The colour is a second signal only — the label beside it
 *  already says which figure this is, so nothing here is carried by hue
 *  alone. */
function negative(tile: OverviewTile): boolean {
  return tile.ok && tile.kind === 'money' && (tile.value ?? 0) < 0
}

function figure(tile: OverviewTile): string {
  if (!tile.ok || tile.value === null || tile.value === undefined) return '—'
  return tile.kind === 'count' ? formatNumber(tile.value, 0) : formatCurrency(tile.value)
}

function niceDate(iso?: string | null): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1]} ${y}`
}
